import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../context/CurrencyContext';
import {
  Smartphone, Wifi, WifiOff, QrCode, Copy, Check, ChevronRight,
  ArrowLeft, Loader2, Zap, Clock, Database, ExternalLink, RefreshCw, AlertTriangle,
  ShoppingCart, Globe, Plus, Sparkles, TrendingUp, MapPin
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function EsimCard({ esim, onManage, t, formatPrice }) {
  const [copied, setCopied] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const hasQr = !!esim.qr_code_text;

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-teal-500/20 transition-all" data-testid={`esim-card-${esim.sim_id || 'legacy'}`}>
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <div className="w-11 h-11 bg-teal-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-5 h-5 text-teal-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">{esim.product_name}</h3>
              <div className="flex items-center gap-2 mt-1">
                {esim.sim_id ? (
                  <span className="flex items-center gap-1 text-xs bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-full">
                    <Wifi className="w-3 h-3" /> {t('account.active')}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs bg-slate-500/10 text-slate-400 px-2 py-0.5 rounded-full">
                    <WifiOff className="w-3 h-3" /> Legacy
                  </span>
                )}
              </div>
              {esim.iccid && (
                <button onClick={() => copy(esim.iccid, 'iccid')} className="flex items-center gap-1 mt-1.5 text-xs text-slate-500 hover:text-teal-400 transition-colors">
                  {copied === 'iccid' ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                  <span className="font-mono">{esim.iccid}</span>
                </button>
              )}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <span className="text-teal-400 font-bold font-mono text-sm">{formatPrice(esim.price)}</span>
            <p className="text-slate-500 text-xs mt-0.5">
              {new Date(esim.purchased_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
            </p>
          </div>
        </div>

        {/* QR Code Toggle */}
        {hasQr && (
          <div className="mt-4">
            <button
              onClick={() => setShowQr(!showQr)}
              className="w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl py-2.5 text-sm text-slate-300 transition-all"
              data-testid={`toggle-qr-${esim.sim_id}`}
            >
              <QrCode className="w-4 h-4 text-teal-400" />
              {showQr ? t('account.hideQr') : t('account.viewQr')}
            </button>
            <AnimatePresence>
              {showQr && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 bg-white rounded-xl p-4 text-center">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(esim.qr_code_text)}`}
                      alt="eSIM QR Code"
                      className="mx-auto w-[180px] h-[180px]"
                    />
                    <p className="text-slate-600 text-xs mt-2">{t('account.scanQr')}</p>
                  </div>
                  {/* SIM details */}
                  <div className="mt-3 space-y-1.5">
                    {[
                      { label: 'SM-DP+', value: esim.smdp_address },
                      { label: 'Activation', value: esim.matching_id },
                      { label: 'APN', value: esim.apn || 'globaldata' },
                    ].filter(d => d.value).map(d => (
                      <div key={d.label} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-1.5">
                        <span className="text-slate-500 text-xs">{d.label}</span>
                        <button onClick={() => copy(d.value, d.label)} className="flex items-center gap-1 text-xs text-slate-300 hover:text-teal-400 font-mono">
                          {copied === d.label ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                          {d.value.length > 30 ? d.value.slice(0, 30) + '...' : d.value}
                        </button>
                      </div>
                    ))}
                    {esim.universal_link && (
                      <a href={esim.universal_link} target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 bg-teal-500/10 hover:bg-teal-500/20 text-teal-400 rounded-lg py-2 text-xs transition-all">
                        <ExternalLink className="w-3 h-3" /> Install via link (iPhone)
                      </a>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Manage Button */}
        {esim.sim_id && (
          <button
            onClick={() => onManage(esim)}
            className="mt-3 w-full flex items-center justify-center gap-2 bg-teal-500/10 hover:bg-teal-500/20 border border-teal-500/20 rounded-xl py-2.5 text-sm text-teal-400 font-medium transition-all"
            data-testid={`manage-esim-${esim.sim_id}`}
          >
            <Zap className="w-4 h-4" />
            {t('account.topup')}
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function TopupView({ esim, onBack, t, formatPrice }) {
  const [canTopup, setCanTopup] = useState(null);
  const [checking, setChecking] = useState(true);
  const [packages, setPackages] = useState([]);
  const [loadingPkgs, setLoadingPkgs] = useState(false);
  const [usage, setUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [paying, setPaying] = useState(null);

  const token = localStorage.getItem('session_token');
  const headers = { Authorization: `Bearer ${token}` };

  const checkTopup = useCallback(async () => {
    setChecking(true);
    try {
      const res = await axios.post(`${API}/esim/${esim.sim_id}/can-topup`, {}, { headers });
      setCanTopup(res.data.can_topup);
    } catch {
      setCanTopup(false);
    }
    setChecking(false);
  }, [esim.sim_id]);

  const fetchUsage = useCallback(async () => {
    setLoadingUsage(true);
    try {
      const res = await axios.get(`${API}/esim/${esim.sim_id}/usage`, { headers });
      setUsage(res.data);
    } catch { /* ignore */ }
    setLoadingUsage(false);
  }, [esim.sim_id]);

  const fetchPackages = useCallback(async () => {
    setLoadingPkgs(true);
    try {
      const res = await axios.get(`${API}/esim/${esim.sim_id}/topup-packages`, {
        headers,
        params: { country: esim.country }
      });
      setPackages(res.data.packages || []);
    } catch { /* ignore */ }
    setLoadingPkgs(false);
  }, [esim.sim_id, esim.country]);

  useEffect(() => {
    checkTopup();
    fetchUsage();
    fetchPackages();
  }, [checkTopup, fetchUsage, fetchPackages]);

  const handleTopup = async (pkg) => {
    setPaying(pkg.id);
    try {
      const res = await axios.post(`${API}/esim/topup/create-payment`, {
        sim_id: esim.sim_id,
        package_type_id: pkg.id,
        package_name: pkg.name,
        package_price: pkg.price_usd,
      }, { headers });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch {
      setPaying(null);
    }
  };

  return (
    <div data-testid="topup-view">
      {/* Back button + Title */}
      <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-teal-400 mb-4 transition-colors" data-testid="back-to-list">
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">{t('account.backToList')}</span>
      </button>

      {/* eSIM Info Card */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
            <Smartphone className="w-6 h-6 text-teal-400" />
          </div>
          <div>
            <h2 className="text-white font-semibold">{esim.product_name}</h2>
            {esim.iccid && <p className="text-slate-500 text-xs font-mono">{esim.iccid}</p>}
          </div>
        </div>

        {/* Usage / Active Packages */}
        {loadingUsage ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> {t('account.loadingDetails')}
          </div>
        ) : usage?.packages?.length > 0 ? (
          <div className="space-y-2">
            {usage.packages.map((pkg, i) => (
              <div key={i} className="bg-white/5 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Database className="w-4 h-4 text-teal-400" />
                    <span className="text-white text-sm font-medium">{pkg.name || `${pkg.data_quantity} ${pkg.data_unit}`}</span>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${pkg.status === 'in_use' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
                    {pkg.status === 'in_use' ? t('account.activePlan') : 'Assigned'}
                  </span>
                </div>
                {pkg.end_time && (
                  <div className="flex items-center gap-1 mt-1.5 text-xs text-slate-500">
                    <Clock className="w-3 h-3" />
                    {(() => {
                      const expiryStr = pkg.date_expiry || pkg.end_time;
                      const expiry = new Date(expiryStr.replace(' ', 'T'));
                      const daysLeft = Math.ceil((expiry - new Date()) / (1000 * 60 * 60 * 24));
                      const color = daysLeft <= 1 ? 'text-rose-400' : daysLeft <= 3 ? 'text-amber-400' : daysLeft <= 5 ? 'text-orange-300' : 'text-slate-500';
                      return (
                        <span className={color}>
                          {t('account.expiresOn')}: {expiry.toLocaleDateString()}
                          {daysLeft > 0 ? ` (${daysLeft}d)` : daysLeft === 0 ? ' (oggi!)' : ' (scaduta)'}
                        </span>
                      );
                    })()}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-sm">{t('account.noActivePlan')}</p>
        )}
      </div>

      {/* Top-up Availability */}
      {checking ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm mb-4">
          <Loader2 className="w-4 h-4 animate-spin" /> {t('account.checkingTopup')}
        </div>
      ) : canTopup === false ? (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-5 mb-6">
          <div className="flex items-start gap-3">
            <div className="bg-amber-500/20 rounded-full p-2 mt-0.5 shrink-0">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <p className="text-amber-300 font-semibold text-sm mb-1">{t('account.cannotTopup')}</p>
              <p className="text-slate-400 text-xs leading-relaxed">{t('account.cannotTopupReason')}</p>
              <button
                onClick={() => window.location.href = '/?page=shop'}
                className="mt-3 bg-teal-600/20 hover:bg-teal-600/30 border border-teal-500/30 text-teal-300 px-4 py-2 rounded-lg text-xs font-medium transition-all"
                data-testid="buy-new-esim-btn"
              >
                {t('account.buyNewPlan')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Available Packages */}
      <div className="mb-4">
        <h3 className="text-white font-semibold mb-1">{t('account.topupTitle')}</h3>
        <p className="text-slate-400 text-sm mb-4">{t('account.topupDesc')}</p>
      </div>

      {loadingPkgs ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 text-teal-400 animate-spin" />
        </div>
      ) : packages.length === 0 ? (
        <p className="text-slate-500 text-sm text-center py-8">{t('account.cannotTopup')}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {packages.map(pkg => {
            const retailEur = (pkg.price_usd * 1.8).toFixed(2);
            return (
              <div key={pkg.id} className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-teal-500/30 transition-all" data-testid={`topup-pkg-${pkg.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-semibold text-sm">
                    {pkg.data_quantity < 0 ? 'Unlimited' : `${pkg.data_quantity} ${pkg.data_unit}`}
                  </span>
                  <span className="text-teal-400 font-bold font-mono">{formatPrice(parseFloat(retailEur))}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-400 mb-3">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {pkg.validity} {t('account.days')}</span>
                </div>
                <button
                  onClick={() => handleTopup(pkg)}
                  disabled={paying === pkg.id || canTopup === false}
                  className="w-full bg-teal-500 hover:bg-teal-400 disabled:bg-slate-700 disabled:text-slate-500 text-black font-semibold py-2 rounded-xl text-sm transition-all flex items-center justify-center gap-2"
                  data-testid={`topup-buy-${pkg.id}`}
                >
                  {paying === pkg.id ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> {t('account.topupProcessing')}</>
                  ) : (
                    <><Zap className="w-4 h-4" /> {t('account.payTopup')}</>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function MyEsimsTab({ onNavigate }) {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const [esims, setEsims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEsim, setSelectedEsim] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);

  const token = localStorage.getItem('session_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchEsims = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/esim/my-esims`, { headers });
      setEsims(res.data.esims || []);
    } catch { /* ignore */ }
    setLoading(false);
    setRefreshing(false);
  }, []);

  const fetchRecommendations = useCallback(async () => {
    setLoadingRecs(true);
    try {
      const res = await axios.get(`${API}/esim/recommendations`, { headers });
      setRecommendations(res.data.recommendations || []);
    } catch { /* ignore */ }
    setLoadingRecs(false);
  }, []);

  useEffect(() => {
    fetchEsims();
    fetchRecommendations();
  }, [fetchEsims, fetchRecommendations]);

  // Check for topup return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const topupSession = params.get('topup_session');
    if (topupSession) {
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
      // TODO: check topup status
    }
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchEsims();
  };

  if (selectedEsim) {
    return (
      <TopupView
        esim={selectedEsim}
        onBack={() => setSelectedEsim(null)}
        t={t}
        formatPrice={formatPrice}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  if (esims.length === 0) {
    return (
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-12 text-center" data-testid="no-esims">
        <QrCode className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <h3 className="text-white font-semibold mb-2">{t('account.noEsims')}</h3>
        <p className="text-slate-400 text-sm mb-6">{t('account.noEsimsDesc')}</p>
        <button
          onClick={() => onNavigate('shop')}
          className="bg-teal-500 hover:bg-teal-400 text-black font-semibold px-6 py-2.5 rounded-full transition-all"
          data-testid="browse-plans-button"
        >
          {t('account.browsePlans') || 'Browse Plans'}
        </button>
      </div>
    );
  }

  // Extract unique destinations from existing eSIMs
  const destinations = [...new Set(esims.filter(e => e.country).map(e => e.country))];

  return (
    <div data-testid="esims-section">
      {/* Header with refresh */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white font-semibold">{t('account.esims')} ({esims.length})</h2>
        <button onClick={handleRefresh} className="flex items-center gap-1 text-slate-400 hover:text-teal-400 text-sm transition-colors" data-testid="refresh-esims">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Buy New eSIM CTA */}
      <div className="relative overflow-hidden bg-gradient-to-r from-teal-600/20 to-cyan-600/20 border border-teal-500/20 rounded-2xl p-5 mb-6" data-testid="buy-new-esim-cta">
        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-teal-500/20 rounded-xl flex items-center justify-center shrink-0">
              <Plus className="w-5 h-5 text-teal-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">{t('account.buyNewEsim', 'Acquista nuova eSIM')}</h3>
              <p className="text-slate-400 text-xs mt-0.5">{t('account.buyNewEsimDesc', 'Trova il piano perfetto per il tuo prossimo viaggio')}</p>
            </div>
          </div>
          <button
            onClick={() => onNavigate('shop')}
            className="flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-black font-semibold px-5 py-2.5 rounded-xl text-sm transition-all shrink-0"
            data-testid="buy-new-esim-main-btn"
          >
            <ShoppingCart className="w-4 h-4" />
            {t('account.goToShop', 'Vai allo Shop')}
          </button>
        </div>
        {/* Quick destination shortcuts */}
        {destinations.length > 0 && (
          <div className="mt-4 pt-3 border-t border-white/5">
            <p className="text-slate-500 text-xs mb-2">{t('account.buyAgainFor', 'Acquista di nuovo per:')}</p>
            <div className="flex flex-wrap gap-2">
              {destinations.slice(0, 5).map(country => (
                <button
                  key={country}
                  onClick={() => { onNavigate('shop'); setTimeout(() => window.dispatchEvent(new CustomEvent('esim-search', { detail: country })), 100); }}
                  className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-teal-500/30 text-slate-300 hover:text-teal-300 px-3 py-1.5 rounded-lg text-xs transition-all"
                  data-testid={`buy-again-${country}`}
                >
                  <Globe className="w-3 h-3" />
                  {country}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* eSIM Cards */}
      <div className="space-y-4 mb-8">
        {esims.map((esim, i) => (
          <EsimCard
            key={esim.sim_id || `legacy-${i}`}
            esim={esim}
            onManage={setSelectedEsim}
            t={t}
            formatPrice={formatPrice}
          />
        ))}
      </div>

      {/* Recommended eSIMs */}
      {(recommendations.length > 0 || loadingRecs) && (
        <div className="mb-8" data-testid="recommendations-section">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <h3 className="text-white font-semibold">{t('account.recommendedForYou', 'Consigliati per te')}</h3>
          </div>
          {loadingRecs ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> {t('account.loadingRecommendations', 'Caricamento consigli...')}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {recommendations.map((rec) => (
                <motion.div
                  key={rec.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="group bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-teal-500/30 transition-all cursor-pointer"
                  onClick={() => onNavigate('shop')}
                  data-testid={`rec-card-${rec.id}`}
                >
                  {/* Flag / Image */}
                  <div className="h-20 bg-gradient-to-br from-slate-800 to-slate-900 relative overflow-hidden">
                    {rec.flag_url && !rec.is_global ? (
                      <img
                        src={rec.flag_url}
                        alt={rec.country_name}
                        className="w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Globe className="w-8 h-8 text-teal-500/30" />
                      </div>
                    )}
                    {/* Badge */}
                    <div className="absolute top-2 left-2">
                      {rec.recommendation_type === 'history' ? (
                        <span className="flex items-center gap-1 bg-teal-500/80 text-black text-[10px] font-bold px-1.5 py-0.5 rounded-md">
                          <RefreshCw className="w-2.5 h-2.5" /> {t('account.recBuyAgain', 'Riacquista')}
                        </span>
                      ) : rec.recommendation_type === 'popular' ? (
                        <span className="flex items-center gap-1 bg-amber-500/80 text-black text-[10px] font-bold px-1.5 py-0.5 rounded-md">
                          <TrendingUp className="w-2.5 h-2.5" /> {t('account.recPopular', 'Popolare')}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 bg-cyan-500/80 text-black text-[10px] font-bold px-1.5 py-0.5 rounded-md">
                          <MapPin className="w-2.5 h-2.5" /> {t('account.recTrending', 'Tendenza')}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Info */}
                  <div className="p-3">
                    <p className="text-white text-xs font-semibold truncate">{rec.country_name}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-teal-400 text-xs font-mono font-bold">
                        {t('account.recFrom', 'da')} {formatPrice(parseFloat(rec.price))}
                      </span>
                      <span className="text-slate-500 text-[10px]">
                        {rec.variations_count} {t('account.recPlans', 'piani')}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Installation Guide */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <QrCode className="w-5 h-5 text-teal-400" />
          {t('account.installGuide')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white/5 rounded-xl p-4">
            <h4 className="text-white font-medium text-sm mb-2">iPhone</h4>
            <ol className="text-slate-400 text-xs space-y-1.5 list-decimal list-inside">
              {(t('account.iphoneSteps', { returnObjects: true }) || []).map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>
          <div className="bg-white/5 rounded-xl p-4">
            <h4 className="text-white font-medium text-sm mb-2">Android</h4>
            <ol className="text-slate-400 text-xs space-y-1.5 list-decimal list-inside">
              {(t('account.androidSteps', { returnObjects: true }) || []).map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
